import type { VenueOSClient } from '@mappedin/venueos-applet';
import type { AgencyMode, FetchTransitResult, TransitPrediction, TransitStop } from '../types';

// ─── ActionFigure / TransitScreen response shapes ──────────────────────────
//
// Typed deeply-optional because the upstream API returns a heterogeneous
// payload depending on agency mode (masstransit vs docked vs dockless) and
// we only consume a subset of the fields. Anything missing falls back to a
// sensible default in `transformResponse`.

interface RawStyle {
  brandColor?: string | null;
  modeLogoSvg?: string | null;
  logoOndarkSvgUrl?: string | null;
  logoOndarkPngUrl?: string | null;
  logoOnlightSvgUrl?: string | null;
  logoOnlightPngUrl?: string | null;
}

interface RawAgency {
  id: number;
  shortName?: string;
  mode?: string;
  style?: RawStyle | null;
}

interface RawCoordinates {
  latitude: number;
  longitude: number;
}

interface RawPrediction {
  predictionMinutesAway?: number | null;
  shortRoute?: string | null;
  fullRoute?: string | null;
  destination?: string | null;
  routeColor?: string | null;
  textColor?: string | null;
  isEnroute?: boolean | null;
  isStatic?: boolean | null;
  crowding?: TransitPrediction['crowding'] | null;
}

interface RawDockedLocation {
  name: string;
  walkMinutes?: number | null;
  coordinates?: RawCoordinates | null;
}

interface RawDocklessService {
  name: string;
  pointPrediction?: number | null;
}

interface RawEntity {
  id: string;
  type: TransitStop['type'];
  agencyId: number;
  stopName?: string | null;
  walkMinutes?: number | null;
  coordinates?: RawCoordinates | null;
  predictions?: RawPrediction[] | null;
  locations?: RawDockedLocation[] | null;
  services?: RawDocklessService[] | null;
}

interface RawAnywhereResponse {
  data?: {
    entities?: RawEntity[];
    agencies?: RawAgency[];
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function pickAgencyMode(mode: unknown): AgencyMode {
  if (typeof mode !== 'string') return 'bus';
  switch (mode) {
    case 'bus':
    case 'rail':
    case 'subway':
    case 'train':
    case 'streetcar':
    case 'shuttle':
    case 'bikeshare':
    case 'scootershare':
    case 'floating':
      return mode;
    default:
      return 'bus';
  }
}

function pickAgencyLogo(style: RawStyle | null | undefined): string | null {
  if (!style) return null;
  return (
    style.logoOndarkSvgUrl ??
    style.logoOndarkPngUrl ??
    style.logoOnlightSvgUrl ??
    style.logoOnlightPngUrl ??
    null
  );
}

function transformPrediction(pred: RawPrediction): TransitPrediction {
  return {
    minutesAway: pred.predictionMinutesAway ?? 0,
    shortRoute: pred.shortRoute ?? null,
    fullRoute: pred.fullRoute ?? null,
    destination: pred.destination ?? null,
    routeColor: pred.routeColor ?? null,
    textColor: pred.textColor ?? null,
    isEnroute: !!pred.isEnroute,
    isStatic: !!pred.isStatic,
    crowding: pred.crowding ?? null,
  };
}

interface AgencyContext {
  agencyId: number;
  agencyName: string;
  agencyMode: AgencyMode;
  brandColor: string;
  logoUrl: string | null;
  modeIconUrl: string | null;
}

function buildAgencyContext(agency: RawAgency): AgencyContext {
  return {
    agencyId: agency.id,
    agencyName: agency.shortName ?? 'Unknown',
    agencyMode: pickAgencyMode(agency.mode),
    brandColor: agency.style?.brandColor ?? '#3b82f6',
    logoUrl: pickAgencyLogo(agency.style),
    modeIconUrl: agency.style?.modeLogoSvg ?? null,
  };
}

function buildMasstransitStop(entity: RawEntity, ctx: AgencyContext): TransitStop | null {
  if (!entity.coordinates || !entity.stopName) return null;
  return {
    id: entity.id,
    name: entity.stopName,
    type: 'masstransit',
    walkMinutes: entity.walkMinutes ?? null,
    latitude: entity.coordinates.latitude,
    longitude: entity.coordinates.longitude,
    ...ctx,
    predictions: (entity.predictions ?? []).map(transformPrediction),
  };
}

function buildDockedStops(entity: RawEntity, ctx: AgencyContext): TransitStop[] {
  return (entity.locations ?? [])
    .filter((loc): loc is RawDockedLocation & { coordinates: RawCoordinates } => !!loc.coordinates)
    .map((loc) => ({
      id: `${entity.id}_${loc.name}`,
      name: loc.name,
      type: 'docked',
      walkMinutes: loc.walkMinutes ?? null,
      latitude: loc.coordinates.latitude,
      longitude: loc.coordinates.longitude,
      ...ctx,
    }));
}

function buildDocklessStops(
  entity: RawEntity,
  ctx: AgencyContext,
  fallbackLat: number,
  fallbackLng: number,
): TransitStop[] {
  return (entity.services ?? []).map((svc) => ({
    id: `${entity.id}_${svc.name}`,
    name: `${ctx.agencyName} — ${svc.name}`,
    type: 'dockless',
    walkMinutes: svc.pointPrediction ?? null,
    latitude: fallbackLat,
    longitude: fallbackLng,
    ...ctx,
  }));
}

function transformEntity(
  entity: RawEntity,
  ctx: AgencyContext,
  fallbackLat: number,
  fallbackLng: number,
): TransitStop[] {
  switch (entity.type) {
    case 'masstransit': {
      const stop = buildMasstransitStop(entity, ctx);
      return stop ? [stop] : [];
    }
    case 'docked':
      return buildDockedStops(entity, ctx);
    case 'dockless':
      return buildDocklessStops(entity, ctx, fallbackLat, fallbackLng);
    default:
      return [];
  }
}

function transformResponse(
  payload: RawAnywhereResponse,
  fallbackLat: number,
  fallbackLng: number,
): TransitStop[] {
  const entities = payload.data?.entities ?? [];
  const agencies = payload.data?.agencies ?? [];
  const agencyById = new Map<number, RawAgency>(agencies.map((a) => [a.id, a]));

  const stops: TransitStop[] = [];
  for (const entity of entities) {
    const agency = agencyById.get(entity.agencyId);
    if (!agency) continue;
    const ctx = buildAgencyContext(agency);
    stops.push(...transformEntity(entity, ctx, fallbackLat, fallbackLng));
  }
  return stops;
}

function isRawAnywhereResponse(value: unknown): value is RawAnywhereResponse {
  if (!isRecord(value)) return false;
  if (!('data' in value)) return true;
  const data = value['data'];
  return data === undefined || isRecord(data);
}

function extractServerErrorMessage(data: unknown): string | null {
  if (typeof data === 'string' && data.length > 0) return data;
  if (!isRecord(data)) return null;
  const candidates = ['message', 'error', 'detail'] as const;
  for (const key of candidates) {
    const v = data[key];
    if (typeof v === 'string' && v.length > 0) return v;
  }
  return null;
}

/**
 * Fetch realtime transit data for a given lat/lng.
 *
 * Always hits the server-side proxy `GET /api/transit/anywhere` (which uses
 * the deployment-wide `ACTIONFIGURE_API_KEY` to call TransitScreen). There
 * is no demo / mock fallback — if the API can't be reached, this throws so
 * the UI can surface the actual failure instead of silently rendering fake
 * stops.
 *
 * Common failure modes the thrown error distinguishes:
 *   - Applet not embedded in VenueOS (no client available).
 *   - Server proxy returned a non-2xx (e.g. 503 when the API key is unset,
 *     502 if TransitScreen itself rejected the request).
 *   - Server proxy returned a 2xx but the body wasn't a recognisable
 *     TransitScreen payload.
 *   - Network error reaching the shell / server.
 */
export async function fetchTransitData(
  client: VenueOSClient | null,
  lat: number,
  lng: number,
): Promise<FetchTransitResult> {
  if (!client) {
    throw new Error('Transit data is unavailable outside the VenueOS shell.');
  }

  const coords = `${lat},${lng}`;
  const path = `/api/transit/anywhere?coordinates=${encodeURIComponent(coords)}`;

  let response: Awaited<ReturnType<VenueOSClient['api']['fetch']>>;
  try {
    response = await client.api.fetch(path);
  } catch (err) {
    const detail = err instanceof Error ? err.message : 'unknown error';
    const wrapped = new Error(`Transit API request failed: ${detail}`);
    Object.assign(wrapped, { cause: err });
    throw wrapped;
  }

  if (!response.ok) {
    const serverMessage = extractServerErrorMessage(response.data);
    const suffix = serverMessage ? ` (${serverMessage})` : '';
    throw new Error(`Transit API returned ${response.status}${suffix}`);
  }

  if (!isRawAnywhereResponse(response.data)) {
    throw new Error('Transit API returned an unexpected payload.');
  }

  const stops = transformResponse(response.data, lat, lng);
  return { stops, source: 'actionfigure' };
}
